/**
 * Tax Planning — shared constants & helpers.
 *
 * Point product/tech requirements at this module + TaxPlanningModal.jsx.
 * Entry points:
 *   - Payroll → row Actions → Tax Planning
 *   - Requests (right bar) → Request Types → Tax Planning
 */

import {
  uiPropertyTypeToOccupancy,
  isRentablePropertyType,
  occupancyToUiPropertyType,
} from "../../../utils/oldRegimeTaxCalc";

export { occupancyToUiPropertyType, uiPropertyTypeToOccupancy };

export const TAX_PLANNING_TITLE = "Tax Planning";

/** Labels that should open the Tax Planning form (incl. legacy aliases). */
export const TAX_PLANNING_ACTION_LABELS = [
  "Tax Planning",
  "Tax Declaration",
  "Investment Declaration",
];

export const TAX_PLANNING_SECTION_DEFS = [
  { id: "prevEmployment", label: "Previous Employment" },
  { id: "hra", label: "HRA" },
  { id: "section80C", label: "Section 80C" },
  { id: "viaDeductions", label: "VIA-deductions" },
  { id: "houseProperty", label: "House Property" },
];

/** Indian FY string e.g. "2026-2027" (April–March). */
export function getCurrentFinancialYear(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 1-12
  // Match Payroll list format: 2026-27 (not 2026-2027)
  if (month >= 4) {
    return `${year}-${String((year + 1) % 100).padStart(2, "0")}`;
  }
  return `${year - 1}-${String(year % 100).padStart(2, "0")}`;
}

/** Pay month YYYY-MM → Indian FY (Apr–Mar), e.g. 2026-01 → 2025-26. */
export function financialYearFromPayMonth(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return "";
  const y = Number(m[1]);
  const month = Number(m[2]);
  if (month >= 4) return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  return `${y - 1}-${String(y % 100).padStart(2, "0")}`;
}

/**
 * As-of pay month for Tax Planning / TDS liability / Full TDS computation.
 * Prefer explicit payroll payMonth so Jan-26 payroll never drifts to calendar Aug-26.
 */
export function resolveTaxPlanningPayMonth({
  payMonth = null,
  financialYear = null,
  now = new Date(),
} = {}) {
  const pm = String(payMonth || "").slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(pm)) return pm;

  const fyKey = normalizeFinancialYearKey(financialYear);
  const fyMatch = String(fyKey || "").match(/^(\d{4})-(\d{2})$/);
  if (fyMatch) {
    const startY = Number(fyMatch[1]);
    const endY = startY + 1;
    const fyStart = `${startY}-04`;
    const fyEnd = `${endY}-03`;
    const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    if (nowKey >= fyStart && nowKey <= fyEnd) return nowKey;
    if (nowKey > fyEnd) return fyEnd;
    return fyStart;
  }

  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** Normalize "2026-27" and "2026-2027" to the same key for lock comparisons. */
export function normalizeFinancialYearKey(fy) {
  const s = String(fy || "").trim();
  const m = s.match(/^(\d{4})-(\d{2}|\d{4})$/);
  if (!m) return s;
  const start = Number(m[1]);
  let end = Number(m[2]);
  if (end < 100) {
    end = Math.floor(start / 100) * 100 + end;
    if (end < start) end += 100;
  }
  return `${start}-${String(end % 100).padStart(2, "0")}`;
}

export function financialYearsMatch(a, b) {
  if (!a || !b) return false;
  return normalizeFinancialYearKey(a) === normalizeFinancialYearKey(b);
}

/** FY string for a join / calendar date (Indian April–March). */
export function getFinancialYearForDate(rawDate) {
  if (!rawDate) return null;
  const d = new Date(rawDate);
  if (Number.isNaN(d.getTime())) return null;
  return getCurrentFinancialYear(d);
}

/** True when employee's DOJ falls in the given financial year. */
export function isJoiningFinancialYear(employeeOrProfile, financialYear) {
  const doj =
    employeeOrProfile?.dateOfJoin ||
    employeeOrProfile?.joiningDate ||
    employeeOrProfile?.joining_date ||
    employeeOrProfile?.date_of_joining ||
    null;
  const joiningFy = getFinancialYearForDate(doj);
  if (!joiningFy || !financialYear) return false;
  return financialYearsMatch(joiningFy, financialYear);
}

/** Resolve numeric employee id from employee object / context. */
export function resolveEmployeeId(employeeOrContext) {
  if (!employeeOrContext) return null;
  const raw =
    employeeOrContext.id ??
    employeeOrContext.employeeId ??
    employeeOrContext.employee_id ??
    null;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function resolveTaxRegime(raw) {
  if (!raw) return "new";
  return String(raw).toLowerCase() === "old" ? "old" : "new";
}

export function isTaxPlanningLabel(label) {
  return TAX_PLANNING_ACTION_LABELS.includes(label);
}

export function resolveFormActionLabel(label) {
  if (isTaxPlanningLabel(label)) return TAX_PLANNING_TITLE;
  return label;
}

/**
 * Placeholder employee so the Tax Planning UI can open before
 * Request-Types employee wiring is complete.
 */
export function buildPlaceholderEmployee(context = {}) {
  const nameParts = String(context?.userName || "Current user")
    .trim()
    .split(/\s+/);
  const firstName = nameParts[0] || "Current";
  const lastName = nameParts.slice(1).join(" ") || "";
  const numericId = resolveEmployeeId(context);
  return {
    id: numericId,
    employeeId: numericId,
    empCode: context?.empCode || "",
    taxRegime: "new",
    dateOfJoin: context?.dateOfJoin || context?.joiningDate || null,
    user: {
      name: firstName,
      lastName,
      email: context?.userEmail || "",
    },
  };
}

export function isTaxPlanningSubmitPayload(updatedData) {
  return Boolean(
    updatedData &&
      (updatedData.type === "submitted" ||
        updatedData.type === "submit_success")
  );
}

/** True when Declaration settings require an approval chain. */
export function isDeclarationApprovalWorkflowRequired(payrollSettings) {
  const raw = payrollSettings?.declarationWorkflow;
  if (!raw || typeof raw !== "object") return false;
  if (raw.approvalMethod !== "workflow") return false;
  return Array.isArray(raw.workflowSteps) && raw.workflowSteps.some((s) => String(s?.role || "").trim());
}

/** Flatten declaration for Request Status Desk (avoid [object Object]). */
export function buildTaxPlanningDeskFormData({
  taxRegime = "new",
  financialYear = "",
  declarationData = {},
  prevEmployment = null,
  status = "SUBMITTED",
} = {}) {
  const regime = resolveTaxRegime(taxRegime);
  const summary = {
    financialYear: financialYear || "",
    taxRegime: regime === "old" ? "OLD" : "NEW",
    status: status || "SUBMITTED",
  };

  if (prevEmployment) {
    if (prevEmployment.employerName) summary.prevEmployerName = prevEmployment.employerName;
    if (prevEmployment.employerPan) summary.prevEmployerPan = prevEmployment.employerPan;
    if (prevEmployment.grossSalary) summary.prevGrossSalary = prevEmployment.grossSalary;
    if (prevEmployment.taxDeducted) summary.prevTaxDeducted = prevEmployment.taxDeducted;
    if (prevEmployment.professionalTax)
      summary.prevProfessionalTax = prevEmployment.professionalTax;
  }

  if (regime !== "old") {
    summary.note = "New regime — no investment deductions declared";
    return summary;
  }

  const hra = Array.isArray(declarationData.hraEntries) ? declarationData.hraEntries : [];
  const hraParts = hra
    .filter((e) => Number(e?.totalRentPaid) > 0 || e?.landlordPan || e?.rentalAddress)
    .map((e, i) => {
      const bits = [
        e.cityType === "metro" ? "Metro" : "Non-metro",
        e.totalRentPaid ? `Rent ${e.totalRentPaid}` : null,
        e.landlordPan ? `PAN ${e.landlordPan}` : null,
      ].filter(Boolean);
      return `#${i + 1}: ${bits.join(", ")}`;
    });
  if (hraParts.length) summary.hra = hraParts.join(" | ");

  const s80 = declarationData.section80C || {};
  const s80Parts = Object.entries(s80)
    .filter(([, v]) => v !== "" && v != null && Number(v) !== 0)
    .map(([k, v]) => `${k}: ${v}`);
  if (s80Parts.length) summary.section80C = s80Parts.join("; ");

  const via = declarationData.sectionVIA || {};
  const viaBits = [];
  const d80 = via.section80D || {};
  Object.entries(d80).forEach(([k, v]) => {
    if (k === "isParentsSeniorCitizen") {
      if (v) viaBits.push("Parents senior citizen: Yes");
      return;
    }
    if (v !== "" && v != null && Number(v) !== 0) viaBits.push(`80D ${k}: ${v}`);
  });
  const other = via.otherInvestments || {};
  Object.entries(other).forEach(([k, v]) => {
    if (v !== "" && v != null && Number(v) !== 0) viaBits.push(`${k}: ${v}`);
  });
  if (viaBits.length) summary.sectionVIA = viaBits.join("; ");

  const hp = Array.isArray(declarationData.housePropertyEntries)
    ? declarationData.housePropertyEntries
    : [];
  const hpParts = hp
    .filter((e) => Number(e?.interest) > 0 || e?.propertyType)
    .map((e, i) => {
      const label =
        e.propertyType === "deemed-let-out"
          ? "deemed let-out"
          : e.propertyType || "self-occupied";
      const bits = [
        label,
        e.interest ? `Interest ${e.interest}` : null,
        isRentablePropertyType(e.propertyType) && e.grossAnnualValue
          ? `GAV ${e.grossAnnualValue}`
          : null,
      ].filter(Boolean);
      return `#${i + 1}: ${bits.join(", ")}`;
    });
  if (hpParts.length) summary.houseProperty = hpParts.join(" | ");

  return summary;
}

/** API body sections for saveInvestmentData from modal state. */
export function buildInvestmentSectionsPayload(declarationData = {}) {
  const hra = Array.isArray(declarationData.hraEntries)
    ? declarationData.hraEntries.map((entry) => ({
        isMetro: entry.cityType === "metro",
        totalRentPaid: parseFloat(entry.totalRentPaid) || 0,
        landlordPan: entry.landlordPan,
        rentalAddress: entry.rentalAddress,
      }))
    : [];

  const s = declarationData.section80C || {};
  const section80c = {
    lifeInsurancePremium: parseFloat(s.lifeInsurance) || 0,
    ppf: parseFloat(s.ppf) || 0,
    vpf: parseFloat(s.vpf) || 0,
    pensionFund: parseFloat(s.pensionFund) || 0,
    nsc: parseFloat(s.nsc) || 0,
    nscInterestReinvested: parseFloat(s.nscInterest) || 0,
    ulip: parseFloat(s.ulip) || 0,
    elss: parseFloat(s.elss) || 0,
    tuitionFees: parseFloat(s.tuitionFees) || 0,
    principalHousingLoan: parseFloat(s.houseLoanPrincipal) || 0,
    registrationCharges: parseFloat(s.registrationCharges) || 0,
    sukanyaSamriddhi: parseFloat(s.sukanyaSamriddhi) || 0,
    infrastructureBonds: parseFloat(s.infrastructureBonds) || 0,
    bankFixedDeposit: parseFloat(s.fixedDeposit) || 0,
  };

  const viaSrc = declarationData.sectionVIA || {};
  const d = viaSrc.section80D || {};
  const o = viaSrc.otherInvestments || {};
  const via = {
    selfMedicalInsurance: parseFloat(d.self) || 0,
    parentsMedicalInsurance: parseFloat(d.parents) || 0,
    seniorCitizenParentsMedical: 0,
    isSelfSeniorCitizen: !!d.isSelfSeniorCitizen,
    isParentsSeniorCitizen: !!d.isParentsSeniorCitizen,
    selfPreventiveHealthCheckup: parseFloat(d.selfPreventiveHealthCheckup) || 0,
    parentsPreventiveHealthCheckup: parseFloat(d.parentsPreventiveHealthCheckup) || 0,
    preventiveHealthCheckup: 0,
    interestEducationLoan: parseFloat(o.section80E) || 0,
    npsAdditionalContribution: parseFloat(o.section80CCD1B) || 0,
    interestOnHomeLoan80EE: parseFloat(o.section80EE) || 0,
    interestOnHomeLoan80EEA: parseFloat(o.section80EEA) || 0,
    interestOnHomeLoan80EEB: parseFloat(o.section80EEB) || 0,
    donations80G: 0,
    interestOnSavings80TTA: parseFloat(o.section80TTA) || 0,
    interestOnDeposits80TTB: 0,
  };

  const houseProperty = Array.isArray(declarationData.housePropertyEntries)
    ? declarationData.housePropertyEntries.map((entry) => {
        const occupancy = uiPropertyTypeToOccupancy(entry.propertyType);
        const rentable = isRentablePropertyType(entry.propertyType);
        return {
          occupancy,
          loanBeforeApr1999: !!entry.loanTakenBeforeApril1999,
          interestPaidOnBorrowedCapital: parseFloat(entry.interest) || 0,
          grossAnnualValue: rentable ? parseFloat(entry.grossAnnualValue) || 0 : 0,
          municipalTaxes: rentable ? parseFloat(entry.municipalTaxes) || 0 : 0,
          propertyAddress: entry.address || "",
        };
      })
    : [];

  return { hra, section80c, via, houseProperty };
}
